import React, { useState, useEffect } from 'react';
import {
    Select,
    Input,
    Tag,
    Modal,
    Tooltip,
    Pagination,
    Spin,
    Button,
    message,
} from 'antd';
import { UnorderedListOutlined } from '@ant-design/icons';
import { useGetAllCarts, getCartDetails } from '../../hooks/useOrders';
import CommonTable from '../../commonComponents/Table/table';
import SupportArticleCartItems from '../../components/SupportArticleCartItems';
import './style.scss';

const STAGE_OPTIONS = [
    { value: 'created', label: 'Created' },
    { value: 'issued', label: 'Issued' },
    { value: 'closed', label: 'Closed' },
    { value: 'partial issued', label: 'Partial Issued' },
];

const AllCSACarts = () => {
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [cartStage, setCartStage] = useState(undefined);
    const [displayName, setDisplayName] = useState('');
    const [selectedCart, setSelectedCart] = useState(null);
    const [carts, setCarts] = useState([]);
    const [total, setTotal] = useState(0);
    const [cartForModal, setCartForModal] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const onSuccessGetAllCarts = (res) => {
        const list = res?.data?.data ?? res?.data ?? [];
        const count = res?.data?.totalCount ?? res?.totalCount ?? 0;
        setCarts(Array.isArray(list) ? list : []);
        setTotal(typeof count === 'number' ? count : 0);
    };
    const onErrorGetAllCarts = (err) => {
        message.error(err?.response?.data?.message ?? 'Failed to load carts');
    };

    const { isLoading: isLoadingCarts } = useGetAllCarts(
        pageNum,
        pageSize,
        cartStage,
        displayName,
        onSuccessGetAllCarts,
        onErrorGetAllCarts,
    );

    useEffect(() => {
        if (!selectedCart) {
            setCartForModal(null);
            return;
        }
        setModalLoading(true);
        setCartForModal(null);
        getCartDetails(selectedCart._id)
            .then((response) => {
                const data = response?.data?.data ?? response?.data ?? selectedCart;
                setCartForModal(data ?? selectedCart);
            })
            .catch(() => {
                setCartForModal(selectedCart);
            })
            .finally(() => {
                setModalLoading(false);
            });
    }, [selectedCart]);

    const handleOpenItemsModal = (cart) => {
        setSelectedCart(cart);
    };

    const handleCloseItemsModal = () => {
        setSelectedCart(null);
    };

    const spinIcon = (
        <div className="all-csa-carts-spin-center">
            <Spin className="all-csa-carts-spin" size="large" />
        </div>
    );

    const columns = [
        {
            title: 'Cart Name',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (val) => val ?? '—',
        },
        {
            title: 'Stage',
            dataIndex: 'cartStage',
            key: 'cartStage',
            render: (stage) =>
                stage ? (
                    <Tag color={stage === 'closed' || stage === 'Completed' ? 'green' : 'blue'}>
                        {stage}
                    </Tag>
                ) : (
                    '—'
                ),
        },
        {
            title: 'Total Issued',
            dataIndex: 'issuedCSACount',
            key: 'issuedCSACount',
            render: (val) => (val != null ? val : '—'),
        },
        {
            title: 'Returned',
            dataIndex: 'returnedCSACount',
            key: 'returnedCSACount',
            render: (val) => (val != null ? val : '—'),
        },
        {
            title: 'Issued To',
            key: 'issuedTo',
            render: (_, record) => {
                const to = record?.issuedTo;
                if (!to) return '—';
                const name = to?.name ?? to?.email ?? '—';
                const email = to?.email;
                return email ? `${name} (${email})` : name;
            },
        },
        {
            title: 'Issued By',
            key: 'issuedBy',
            render: (_, record) => {
                const by = record?.issuedBy;
                if (!by) return '—';
                const name = by?.name ?? by?.email ?? '—';
                const email = by?.email;
                return email ? `${name} (${email})` : name;
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Tooltip title="View cart items">
                    <Button
                        type="link"
                        size="small"
                        icon={<UnorderedListOutlined />}
                        onClick={() => handleOpenItemsModal(record)}
                    >
                        View items
                    </Button>
                </Tooltip>
            ),
        },
    ];

    const showTable = !isLoadingCarts && carts?.length > 0;
    const showEmpty = !isLoadingCarts && (!carts || carts.length === 0);
    const showPagination = !isLoadingCarts && (carts?.length > 0 || total > 0);

    return (
        <div className="all-csa-carts-main">
            <div className="all-csa-carts-header">
                <h1 className="all-csa-carts-title">All CSA Carts</h1>
                <div className="all-csa-carts-filters">
                    <Select
                        placeholder="All stages"
                        allowClear
                        value={cartStage ?? undefined}
                        onChange={(v) => {
                            setCartStage(v ?? undefined);
                            setPageNum(1);
                        }}
                        options={STAGE_OPTIONS}
                        className="all-csa-carts-stage-select"
                    />
                    <Input
                        placeholder="Search by cart name"
                        value={displayName}
                        onChange={(e) => {
                            setDisplayName(e.target.value);
                            setPageNum(1);
                        }}
                        allowClear
                        className="all-csa-carts-search-input"
                    />
                </div>
            </div>

            {showEmpty && <div className="all-csa-carts-empty">No cart available</div>}

            {showTable && (
                <>
                    <div className="all-csa-carts-table-wrap">
                        <CommonTable
                            columns={columns}
                            data={carts}
                            rowKey={(record) => record?._id ?? record?.id}
                            loading={{
                                spinning: isLoadingCarts,
                                indicator: spinIcon,
                            }}
                            pagination={false}
                            bordered
                        />
                    </div>
                    {showPagination && (
                        <div className="all-csa-carts-pagination">
                            <Pagination
                                current={pageNum}
                                pageSize={pageSize}
                                total={total}
                                showSizeChanger
                                onChange={(page, size) => {
                                    setPageNum(page);
                                    setPageSize(size || pageSize);
                                }}
                            />
                        </div>
                    )}
                </>
            )}

            <Modal
                open={!!selectedCart}
                onCancel={handleCloseItemsModal}
                footer={null}
                width={800}
                className="all-csa-carts-items-modal"
                destroyOnClose
                title={null}
            >
                {modalLoading ? (
                    <div className="all-csa-carts-modal-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    selectedCart && (
                        <SupportArticleCartItems
                            cart={cartForModal ?? selectedCart}
                        />
                    )
                )}
            </Modal>
        </div>
    );
};

export default AllCSACarts;
